"use strict";
/**
 * Email template system for AyurCRM / Vaidban notifications.
 *
 * Each template returns { subject, contentHtml } where contentHtml is the
 * inner content block. The baseLayout() wrapper adds the consistent
 * header (logo, nav) and footer (unsubscribe, contact, legal).
 *
 * All styles are inline — required for email client compatibility.
 * Tested layout targets: Gmail, Outlook 2019+, Apple Mail, Yahoo Mail.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.emailBuilders = void 0;
exports.buildEmail = buildEmail;
// ── Design tokens ─────────────────────────────────────────────────────────────
const BRAND_GREEN = '#234232';
const BRAND_GOLD = '#d8b36a';
const TEXT_DARK = '#2e2417';
const TEXT_MID = '#6b5a3d';
const TEXT_LIGHT = '#9e8a68';
const BG_WARM = '#f6f0e4';
const BG_WHITE = '#ffffff';
const BORDER_COLOR = '#e8d9bc';
// ── Base layout ───────────────────────────────────────────────────────────────
function baseLayout(subject, contentHtml, unsubscribeUrl) {
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>${subject}</title>
</head>
<body style="margin:0;padding:0;background:${BG_WARM};font-family:Arial,Helvetica,sans-serif;color:${TEXT_DARK};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${BG_WARM};">
    <tr>
      <td align="center" style="padding:32px 16px 48px;">

        <!-- ── Header: logo ──────────────────────────────────────────── -->
        <table role="presentation" width="560" style="max-width:560px;width:100%;" cellpadding="0" cellspacing="0">
          <tr>
            <td style="padding-bottom:20px;text-align:center;">
              <img
                src="https://vaidban.com/cdn/shop/files/Vaidban_new.png"
                alt="Vaidban Ayurved Bhawan"
                height="48"
                style="display:block;margin:0 auto;height:48px;object-fit:contain;"
              />
            </td>
          </tr>

          <!-- ── Content card ───────────────────────────────────────── -->
          <tr>
            <td
              style="background:${BG_WHITE};border-radius:16px;padding:36px 40px;
                     box-shadow:0 4px 20px rgba(0,0,0,0.07);border:1px solid ${BORDER_COLOR};">
              ${contentHtml}
            </td>
          </tr>

          <!-- ── Footer ────────────────────────────────────────────── -->
          <tr>
            <td style="padding:24px 0 0;text-align:center;">
              <p style="margin:0 0 6px;font-size:12px;color:${TEXT_LIGHT};">
                Vaidban Ayurved Bhawan &nbsp;|&nbsp; Helpline: 1800-XXX-XXXX
              </p>
              <p style="margin:0;font-size:11px;color:${TEXT_LIGHT};">
                <a href="${unsubscribeUrl}" style="color:${TEXT_LIGHT};text-decoration:underline;">
                  Unsubscribe
                </a>
                &nbsp;&nbsp;·&nbsp;&nbsp;
                <a href="https://vaidban.com/pages/privacy-policy" style="color:${TEXT_LIGHT};text-decoration:underline;">
                  Privacy Policy
                </a>
                &nbsp;&nbsp;·&nbsp;&nbsp;
                <a href="https://vaidban.com" style="color:${TEXT_LIGHT};text-decoration:underline;">
                  vaidban.com
                </a>
              </p>
              <p style="margin:8px 0 0;font-size:10px;color:#bbb;">
                © ${new Date().getFullYear()} Vaidban Ayurved Bhawan. All rights reserved.
              </p>
            </td>
          </tr>
        </table>

      </td>
    </tr>
  </table>
</body>
</html>`;
}
// ── Helper: primary CTA button ────────────────────────────────────────────────
function ctaButton(label, url) {
    return `
<table role="presentation" cellpadding="0" cellspacing="0" style="margin:28px auto;">
  <tr>
    <td style="border-radius:10px;background:${BRAND_GREEN};">
      <a href="${url}"
         style="display:inline-block;padding:14px 32px;font-size:15px;font-weight:bold;
                color:#ffffff;text-decoration:none;border-radius:10px;letter-spacing:0.02em;">
        ${label}
      </a>
    </td>
  </tr>
</table>`;
}
// ── Helper: info row ──────────────────────────────────────────────────────────
function infoRow(label, value) {
    return `
<tr>
  <td style="padding:6px 0;font-size:13px;color:${TEXT_LIGHT};white-space:nowrap;width:140px;">${label}</td>
  <td style="padding:6px 0;font-size:13px;color:${TEXT_DARK};font-weight:600;">${value}</td>
</tr>`;
}
function greet(customerName) {
    return `<p style="margin:0 0 16px;font-size:15px;color:${TEXT_DARK};">
    Hi <strong>${customerName}</strong>,
  </p>`;
}
// ── Individual templates ───────────────────────────────────────────────────────
exports.emailBuilders = {
    evidence_link_sent: (v, u) => {
        const subject = `Action Required: Upload Evidence — Ticket ${v['ticketNumber'] ?? ''}`;
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        We have received your complaint and would like to investigate it promptly.
        Please upload photos and/or an unboxing video so our team can verify your claim.
      </p>
      <table role="presentation" cellpadding="0" cellspacing="0"
             style="width:100%;border:1px solid ${BORDER_COLOR};border-radius:10px;margin-bottom:24px;">
        <tbody>
          ${infoRow('Ticket Number', v['ticketNumber'] ?? '—')}
          ${infoRow('Link expires', '7 days from now')}
        </tbody>
      </table>
      ${ctaButton('Upload Evidence', v['uploadLink'] ?? '#')}
      <p style="margin:20px 0 0;font-size:12px;color:${TEXT_LIGHT};text-align:center;">
        Or copy this link: <a href="${v['uploadLink'] ?? '#'}" style="color:${BRAND_GREEN};word-break:break-all;">
          ${v['uploadLink'] ?? ''}
        </a>
      </p>
      <hr style="border:none;border-top:1px solid ${BORDER_COLOR};margin:24px 0;" />
      <p style="margin:0;font-size:12px;color:${TEXT_LIGHT};line-height:1.6;">
        If you did not raise a complaint or have questions, please call our helpline at
        <strong>1800-XXX-XXXX</strong> and we will assist you.
      </p>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    order_working: (v, u) => {
        const subject = 'Your Order Is Being Processed — Vaidban';
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        We are working on your order and will have it ready for dispatch soon.
        Please cooperate and have a little patience — our team will call you shortly.
      </p>
      <div style="background:#f9f4ea;border-radius:10px;padding:16px 20px;margin-bottom:20px;">
        <p style="margin:0;font-size:13px;color:${TEXT_MID};">
          🌿 Thank you for choosing Vaidban Ayurved Bhawan. Your wellness is our priority.
        </p>
      </div>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    order_dispatched: (v, u) => {
        const subject = `Your Order Has Been Dispatched — ${v['trackingId'] ?? ''}`;
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Great news! Your order has been dispatched and is on its way to you. 🚚
      </p>
      <table role="presentation" cellpadding="0" cellspacing="0"
             style="width:100%;border:1px solid ${BORDER_COLOR};border-radius:10px;margin-bottom:24px;">
        <tbody>
          ${infoRow('Tracking ID', v['trackingId'] ?? '—')}
          ${infoRow('Carrier', v['carrier'] ?? '—')}
        </tbody>
      </table>
      <p style="margin:0;font-size:12px;color:${TEXT_LIGHT};line-height:1.6;">
        Please record a short unboxing video when your order arrives. Report any issues
        within <strong>72 hours</strong> by calling <strong>1800-XXX-XXXX</strong>.
      </p>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    order_delivered: (v, u) => {
        const subject = 'Your Vaidban Order Has Been Delivered';
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Your order has been delivered. We hope you are pleased with your purchase! 📦
      </p>
      <div style="background:#e8f5e9;border-radius:10px;padding:16px 20px;margin-bottom:20px;
                  border-left:4px solid #2e7d32;">
        <p style="margin:0;font-size:13px;color:#2e5c36;font-weight:600;">Important</p>
        <p style="margin:6px 0 0;font-size:13px;color:#355a40;line-height:1.6;">
          Please record a short video while opening the package. If you receive any damaged
          or incorrect items, report within <strong>72 hours</strong> — Helpline: 1800-XXX-XXXX.
        </p>
      </div>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    feedback_form: (v, u) => {
        const subject = 'Share Your Experience — Vaidban';
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Thank you for choosing Vaidban Ayurved Bhawan! Your feedback helps us serve you
        and thousands of other patients better.
      </p>
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        It only takes 2 minutes. Please share your honest experience:
      </p>
      ${ctaButton('Share Feedback', v['formLink'] ?? '#')}`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    feedback_followup: (v, u) => {
        const subject = 'Reminder: We\'d Love Your Feedback — Vaidban';
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        We noticed you haven't had a chance to share your feedback yet. We completely
        understand — life gets busy!
      </p>
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Your experience matters to us and to the patients we serve. Could you spare 2 minutes?
      </p>
      ${ctaButton('Share Feedback Now', v['formLink'] ?? '#')}`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    video_review_request: (v, u) => {
        const subject = 'We\'d Love a Video Review — Vaidban';
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        We are truly delighted by your positive feedback! 🎉
      </p>
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Would you be kind enough to record a short video about your experience?
        Your testimonial helps others discover the benefits of Ayurvedic wellness and
        inspires our team to keep doing their best.
      </p>
      <div style="background:#f9f4ea;border-radius:10px;padding:16px 20px;">
        <p style="margin:0;font-size:13px;color:${TEXT_MID};">
          You can share the video with us via WhatsApp at <strong>+91-XXXXXXXXXX</strong>
          or email it to <strong>support@vaidban.com</strong>.
        </p>
      </div>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    payment_details: (v, u) => {
        const subject = `Payment Details — Vaidban Invoice ₹${v['amount'] ?? ''}`;
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Your invoice is ready. Please complete the payment to confirm your order.
      </p>
      <div style="background:#f9f4ea;border:1px solid ${BORDER_COLOR};border-radius:12px;
                  padding:20px 24px;margin-bottom:24px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="font-size:13px;color:${TEXT_LIGHT};padding-bottom:6px;">Amount Due</td>
          </tr>
          <tr>
            <td style="font-size:28px;font-weight:bold;color:${BRAND_GREEN};padding-bottom:16px;">
              ₹${v['amount'] ?? '—'}
            </td>
          </tr>
          ${infoRow('UPI ID', v['upiId'] ?? '—')}
          ${infoRow('Pay via', 'Any UPI app (GPay, PhonePe, Paytm)')}
        </table>
      </div>
      <p style="margin:0;font-size:12px;color:${TEXT_LIGHT};">
        Once payment is made, please share the payment screenshot with our team.
        Order will be dispatched within 24 hours of payment confirmation.
      </p>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    doc_upload_request: (v, u) => {
        const subject = 'Document Upload Request — Vaidban';
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        To provide you with the best Ayurvedic consultation, our doctors need to review
        your latest medical records.
      </p>
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Please upload any of the following (if available):
      </p>
      <ul style="margin:0 0 20px;padding-left:20px;font-size:14px;color:${TEXT_MID};line-height:1.8;">
        <li>Current prescription from your doctor</li>
        <li>Recent blood test / lab reports</li>
        <li>Any relevant medical history documents</li>
      </ul>
      ${ctaButton('Upload Documents', v['uploadLink'] ?? '#')}`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    appointment_confirmed: (v, u) => {
        const subject = `Appointment Confirmed — ${v['dateTime'] ?? ''}`;
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Your appointment has been confirmed. ✅
      </p>
      <table role="presentation" cellpadding="0" cellspacing="0"
             style="width:100%;border:1px solid ${BORDER_COLOR};border-radius:10px;margin-bottom:24px;">
        <tbody>
          ${infoRow('Doctor', v['doctorName'] ?? '—')}
          ${infoRow('Date & Time', v['dateTime'] ?? '—')}
          ${infoRow('Type', v['type'] ?? '—')}
        </tbody>
      </table>
      <div style="background:#f9f4ea;border-radius:10px;padding:14px 18px;">
        <p style="margin:0;font-size:13px;color:${TEXT_MID};line-height:1.6;">
          Please be available 5 minutes before your appointment time.
          To reschedule, call <strong>1800-XXX-XXXX</strong> at least 2 hours in advance.
        </p>
      </div>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
    website_link: (v, u) => {
        const subject = 'Explore Vaidban Ayurvedic Products';
        const content = `
      ${greet(v['customerName'] ?? 'Customer')}
      <p style="margin:0 0 20px;font-size:14px;color:${TEXT_MID};line-height:1.7;">
        Thank you for your interest in Vaidban Ayurved Bhawan!
        Explore our full range of authentic Ayurvedic products and treatments below.
      </p>
      ${ctaButton('Visit Vaidban.com', v['websiteUrl'] ?? 'https://vaidban.com')}
      <p style="margin:16px 0 0;font-size:12px;color:${TEXT_LIGHT};text-align:center;">
        Our team will follow up with you shortly.
      </p>`;
        return { subject, html: baseLayout(subject, content, u) };
    },
};
/**
 * Build a complete HTML email for the given template key and variables.
 * Returns null if no email template is defined for the key.
 */
function buildEmail(templateKey, variables, unsubscribeUrl) {
    const builder = exports.emailBuilders[templateKey];
    if (!builder)
        return null;
    // Interpolate variables into all string values in the template output
    const result = builder(variables, unsubscribeUrl);
    return {
        subject: interpolate(result.subject, variables),
        html: interpolate(result.html, variables),
    };
}
/** Replace {{key}} placeholders with values from vars. */
function interpolate(template, vars) {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);
}
